import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import { freightQuote } from '../../util/api';
import { trackDeliveryQuote } from '../../analytics/ctTracking';
import { CT_MANAGED_LISTING_TYPE } from '../../util/ctListingDescription';
import FreightQuoteInfo from '../FreightQuoteInfo/FreightQuoteInfo';
import FinancingAvailable from '../FinancingAvailable/FinancingAvailable';
import {
  getFreightSelection,
  setFreightSelection,
  clearFreightSelection,
} from '../../util/ctFreightSelection';

import css from './FreightQuotePanel.module.css';

const HELPLINE_DISPLAY = '1-844-321-2876';
const HELPLINE_TEL = 'tel:+18443212876';
const ZIP_PATTERN = /^\d{5}$/;

const DEALER_BUY_IT_NOW_LISTING_TYPE = 'dealer-buy-it-now';

// Dealer Pro Buy It Now listings get instant quotes when the operator has entered the seller's
// delivery rate card (author metadata.ctDeliveryRateCard). The server checks this again and
// holds the actual rates (server/api-util/dealerDeliveryRates.js).
export const hasDealerRateCard = listing => {
  if (listing?.attributes?.publicData?.listingType !== DEALER_BUY_IT_NOW_LISTING_TYPE) {
    return false;
  }
  const metadata = listing?.author?.attributes?.profile?.metadata || {};
  return (
    metadata.ctSellerPlan === 'pro' &&
    metadata.ctPlanStatus !== 'suspended' &&
    typeof metadata.ctDeliveryRateCard === 'string' &&
    metadata.ctDeliveryRateCard.length > 0
  );
};

const formatCents = cents =>
  typeof cents === 'number'
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100)
    : '';

const pushDataLayer = payload => {
  try {
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    }
  } catch (e) {
    // tracking must never break the page
  }
};

/**
 * Buyer-facing instant tilt-deck delivery quote.
 * Calls POST /api/freight-quote (server keeps the Google key private).
 *
 * @param {Object} props
 * @param {Object} props.listing - current listing entity
 * @param {string} [props.className]
 */
const FreightQuotePanel = props => {
  const { listing, className } = props;
  const [zip, setZip] = useState('');
  const [deliveryType, setDeliveryType] = useState('residential');
  const [inProgress, setInProgress] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [addedToOrder, setAddedToOrder] = useState(false);

  // Restore a delivery already added to this listing's order in this browser session.
  const selectionListingId = listing?.id?.uuid;
  useEffect(() => {
    const saved = getFreightSelection(selectionListingId);
    if (saved) {
      setZip(saved.deliveryZip);
      setDeliveryType(saved.isResidential ? 'residential' : 'commercial');
      setAddedToOrder(true);
    }
  }, [selectionListingId]);

  const geolocation = listing?.attributes?.geolocation;
  const listingId = listing?.id?.uuid;
  // Instant delivery quotes: Container Trader's own (managed) listings, and Dealer Pro
  // Buy It Now listings whose seller has a delivery rate card.
  const isContainerTraderListing =
    listing?.attributes?.publicData?.listingType === CT_MANAGED_LISTING_TYPE;
  const isDealerListing = hasDealerRateCard(listing);
  if (!listingId || !geolocation || !(isContainerTraderListing || isDealerListing)) {
    return null;
  }

  const zipIsValid = ZIP_PATTERN.test(zip);

  const handleSubmit = e => {
    e.preventDefault();
    if (!zipIsValid || inProgress) {
      setError(zipIsValid ? null : 'Enter a valid 5-digit ZIP code.');
      return;
    }
    setInProgress(true);
    setError(null);
    setResult(null);

    freightQuote({
      listingId,
      deliveryZip: zip,
      isResidential: deliveryType === 'residential',
    })
      .then(data => {
        setResult(data);
        // Add delivery to the Buy It Now order so container + delivery are paid together.
        if (!data.requiresApproval && data.freightCents > 0) {
          setFreightSelection(listingId, {
            deliveryZip: zip,
            isResidential: deliveryType === 'residential',
          });
          setAddedToOrder(true);
        } else {
          clearFreightSelection(listingId);
          setAddedToOrder(false);
        }
        pushDataLayer({
          event: 'freight_quote',
          listing_id: listingId,
          delivery_zip: zip,
          delivery_type: deliveryType,
          requires_approval: !!data.requiresApproval,
          road_miles: data.roadMiles || null,
          freight_value: data.freightCents ? data.freightCents / 100 : null,
        });
        trackDeliveryQuote({
          listingId,
          deliveryType,
          requiresApproval: data.requiresApproval,
          deliveredTotalCents: data.deliveredTotalCents,
        });
      })
      .catch(err => {
        setError(
          err?.status === 429
            ? 'Too many quote requests. Please wait a few minutes and try again.'
            : err?.error || 'We could not calculate delivery right now. Please try again.'
        );
      })
      .finally(() => setInProgress(false));
  };

  const handleZipChange = e => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 5);
    setZip(digits);
    if (result) setResult(null);
    if (addedToOrder) {
      clearFreightSelection(listingId);
      setAddedToOrder(false);
    }
    if (error) setError(null);
  };

  const handleTypeChange = e => {
    setDeliveryType(e.target.value);
    if (result) setResult(null);
    if (addedToOrder) {
      clearFreightSelection(listingId);
      setAddedToOrder(false);
    }
  };

  const handleRemoveDelivery = () => {
    clearFreightSelection(listingId);
    setAddedToOrder(false);
  };

  const showPrice = result && !result.requiresApproval;
  const showApproval = result && result.requiresApproval;

  return (
    <section className={classNames(css.root, className)} aria-labelledby="freightQuoteHeading">
      <h2 id="freightQuoteHeading" className={css.heading}>
        Add delivery to your order
      </h2>
      <p className={css.subheading}>
        {isDealerListing
          ? "Tilt-deck delivery from the seller's yard, using the seller's delivery rates. No account needed."
          : 'Tilt-deck delivery to your site. No account needed.'}
      </p>

      <form className={css.form} onSubmit={handleSubmit} noValidate>
        <div className={css.field}>
          <label htmlFor="freightQuoteZip" className={css.label}>
            Delivery ZIP code
          </label>
          <input
            id="freightQuoteZip"
            className={css.zipInput}
            type="text"
            inputMode="numeric"
            autoComplete="postal-code"
            placeholder="e.g. 84720"
            value={zip}
            onChange={handleZipChange}
            aria-invalid={!!error && !zipIsValid}
          />
        </div>

        <fieldset className={css.typeGroup}>
          <legend className={css.label}>Delivery location</legend>
          <label className={css.radioLabel}>
            <input
              type="radio"
              name="freightDeliveryType"
              value="residential"
              checked={deliveryType === 'residential'}
              onChange={handleTypeChange}
            />
            <span>Residential</span>
          </label>
          <label className={css.radioLabel}>
            <input
              type="radio"
              name="freightDeliveryType"
              value="commercial"
              checked={deliveryType === 'commercial'}
              onChange={handleTypeChange}
            />
            <span>Commercial / farm / jobsite</span>
          </label>
        </fieldset>

        <button
          type="submit"
          className={css.submitButton}
          disabled={inProgress || zip.length === 0}
        >
          {inProgress ? 'Calculating…' : 'Get delivery price'}
        </button>
      </form>

      <div aria-live="polite">
        {error ? <p className={css.error}>{error}</p> : null}

        {showPrice ? (
          <div className={css.result}>
            <div className={css.resultRow}>
              <span>Tilt-deck delivery to {result.deliveryZip}</span>
              <strong>{formatCents(result.freightCents)}</strong>
            </div>
            {typeof result.containerPriceCents === 'number' ? (
              <>
                <div className={css.resultRow}>
                  <span>Container price</span>
                  <span>{formatCents(result.containerPriceCents)}</span>
                </div>
                <div className={classNames(css.resultRow, css.totalRow)}>
                  <span>Delivered total</span>
                  <strong>{formatCents(result.deliveredTotalCents)}</strong>
                </div>
              </>
            ) : null}
            <p className={css.note}>
              About {result.roadMiles} road miles from the container&apos;s location.
              {result.isResidential ? ' Includes residential delivery.' : ''} Taxes not included.
              Delivery is confirmed before dispatch.
            </p>
            {addedToOrder ? (
              <div className={css.addedToOrder}>
                <p>
                  <strong>Delivery added to your order.</strong> Click Buy It Now to pay for the
                  container and delivery together by card.
                </p>
                <button type="button" className={css.removeButton} onClick={handleRemoveDelivery}>
                  Remove delivery (I&apos;ll pick it up)
                </button>
              </div>
            ) : (
              <div className={css.addedToOrder}>
                <p>Picking up instead. Delivery won&apos;t be charged at checkout.</p>
                <button
                  type="button"
                  className={css.removeButton}
                  onClick={() => {
                    setFreightSelection(listingId, {
                      deliveryZip: result.deliveryZip,
                      isResidential: !!result.isResidential,
                    });
                    setAddedToOrder(true);
                  }}
                >
                  Add delivery back to my order
                </button>
              </div>
            )}
            {result.dealer ? (
              <p className={css.note}>
                Delivered by {result.dealer.dealerName}
                {result.dealer.leadTimeBusinessDays
                  ? `, usually within ${result.dealer.leadTimeBusinessDays} business days of payment`
                  : ''}
                {result.dealer.deliveryDays ? ` (${result.dealer.deliveryDays})` : ''}. Wait time,
                difficult access and rush delivery are extra if they apply.
              </p>
            ) : null}
          </div>
        ) : null}

        {showApproval ? (
          <div className={css.approval}>
            <p>
              {result.reason === 'over-max-distance'
                ? `This address is more than ${result.maxAutoQuoteMiles ||
                    400} road miles from the container, so delivery needs a custom price.`
                : "We couldn't calculate an automatic quote for this address."}{' '}
              {isDealerListing ? (
                'Message the seller from this listing for a custom delivery quote.'
              ) : (
                <>
                  Call <a href={HELPLINE_TEL}>{HELPLINE_DISPLAY}</a> or contact the seller for a
                  custom delivery quote.
                </>
              )}
            </p>
          </div>
        ) : null}
      </div>

      {addedToOrder && !result ? (
        <div className={css.addedToOrder}>
          <p>
            <strong>Delivery to {zip} is added to your order.</strong> The total is shown above the
            Buy It Now button.
          </p>
          <button type="button" className={css.removeButton} onClick={handleRemoveDelivery}>
            Remove delivery (I&apos;ll pick it up)
          </button>
        </div>
      ) : null}

      <FinancingAvailable />

      <FreightQuoteInfo siteRequirements={result?.dealer?.siteRequirements} />
    </section>
  );
};

export default FreightQuotePanel;
